import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import commafy from 'commafy';

const formatAmount = (amount) => {
  if (amount == null || amount === '' || isNaN(parseFloat(amount))) {
    return null;
  }
  return parseFloat(amount).toFixed(2);
};

export const exportAccountStatementPDF = (data) => {
  if (!data) {
    console.error('No data provided for PDF export');
    return;
  }

  // eslint-disable-next-line new-cap
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Awake Interlink Account Statement', pageWidth / 2, yPosition, {
    align: 'center',
  });

  yPosition += 15;

  // Merchant Information Section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Merchant Information', 14, yPosition);
  yPosition += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  if (data.merchant) {
    const merchantInfo = [
      ['Name:', `${data.merchant.first_name} ${data.merchant.last_name}`],
      ['Email:', data.merchant.email],
      ['Company:', data.merchant.company_name],
      ['Phone:', data.merchant.phone_number],
      ['Company Address:', data.merchant.company_address],
      ['Home Address:', data.merchant.address],
    ];

    merchantInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(value || 'N/A', 50, yPosition);
      yPosition += 6;
    });
  }

  yPosition += 5;

  // Statement Period
  if (data.period) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Statement Period:', 14, yPosition);
    doc.setFont('helvetica', 'normal');
    const startDate = data.period.start_date
      ? dayjs(data.period.start_date).format('MMM D, YYYY')
      : '-';
    const endDate = data.period.end_date
      ? dayjs(data.period.end_date).format('MMM D, YYYY')
      : '-';
    doc.text(`${startDate} - ${endDate}`, 50, yPosition);
    yPosition += 10;
  }

  // Balance Information
  if (data.balance) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Balance Summary', 14, yPosition);
    yPosition += 7;

    doc.setFontSize(10);
    const balanceInfo = [
      [
        'Starting Balance:',
        `N${commafy(formatAmount(data.balance.starting_balance))}`,
      ],
      [
        'Ending Balance:',
        `N${commafy(formatAmount(data.balance.ending_balance))}`,
      ],
    ];

    balanceInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 50, yPosition);
      yPosition += 6;
    });

    yPosition += 5;
  }

  // Transaction Summary
  if (data.summary) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Transaction Summary', 14, yPosition);
    yPosition += 7;

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: [
        ['Total Transactions', data.summary.total_transactions.toString()],
        [
          'Successful Transactions',
          data.summary.successful_transactions.toString(),
        ],
        ['Failed Transactions', data.summary.failed_transactions.toString()],
        ['Pending Transactions', data.summary.pending_transactions.toString()],
        [
          'Total Amount',
          `N${commafy(formatAmount(data.summary.total_amount))}`,
        ],
        ['Total Debit', `N${commafy(formatAmount(data.summary.total_debit))}`],
        [
          'Airtime Total',
          `N${commafy(formatAmount(data.summary.airtime_total))}`,
        ],
        ['Data Total', `N${commafy(formatAmount(data.summary.data_total))}`],
        [
          'Total Funding',
          `N${commafy(formatAmount(data.summary.total_funding))}`,
        ],
        [
          'Funding Transactions',
          data.summary.total_funding_transactions.toString(),
        ],
      ],
      theme: 'grid',
      headStyles: {
        fillColor: [26, 115, 232],
        fontSize: 10,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 9 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 70 },
        1: { cellWidth: 'auto' },
      },
      margin: { left: 14 },
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // Combined Transactions and Funding Table
  const combinedActivities = [];

  // Add funding activities
  if (data.funding && data.funding.length > 0) {
    data.funding.forEach((item) => {
      combinedActivities.push({
        date: item.approved_date || item.requested_date,
        type: 'FUNDING',
        reference: `FND-${item.id || 'N/A'}`,
        description: (item.transaction_type || 'Funding').toUpperCase(),
        amount: item.amount || 0,
        balanceBefore: item.balance_before,
        balanceAfter: item.balance_after,
        status: item.status || 'pending',
        details: item.proof_url ? 'Proof attached' : '',
      });
    });
  }

  // Add transaction activities
  if (data.transactions && data.transactions.length > 0) {
    data.transactions.forEach((item) => {
      const description = `${item.product_name || ''} ${
        item.product_type || ''
      }`.trim();
      combinedActivities.push({
        date: item.transaction_date,
        type: 'TRANSACTION',
        reference: item.reference || 'N/A',
        description: description ? description.toUpperCase() : 'N/A',
        amount: item.debit_amount || 0,
        balanceBefore: item.balance_before,
        balanceAfter: item.balance_after,
        status: item.status || 'pending',
        details: item.phone_number || '',
      });
    });
  }

  // Sort by date in ascending order
  combinedActivities.sort((a, b) => {
    return dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
  });

  // Create combined table
  if (combinedActivities.length > 0) {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`All Transactions (${combinedActivities.length})`, 14, yPosition);
    yPosition += 7;

    const combinedRows = combinedActivities.map((item) => {
      const formattedAmount = formatAmount(item.amount);
      const formattedBalanceBefore = formatAmount(item.balanceBefore);
      const formattedBalanceAfter = formatAmount(item.balanceAfter);
      return [
        item.date ? dayjs(item.date).format('MMM D, YYYY hh:mm A') : 'N/A',
        item.type,
        item.reference,
        item.description,
        formattedAmount != null ? `N${commafy(formattedAmount)}` : 'N/A',
        formattedBalanceBefore != null
          ? `N${commafy(formattedBalanceBefore)}`
          : 'N/A',
        formattedBalanceAfter != null
          ? `N${commafy(formattedBalanceAfter)}`
          : 'N/A',
        item.status,
        item.details,
      ];
    });

    doc.autoTable({
      startY: yPosition,
      head: [
        [
          'Date',
          'Type',
          'Reference',
          'Description',
          'Amount',
          'Balance Before',
          'Balance After',
          'Status',
          'Details',
        ],
      ],
      body: combinedRows,
      theme: 'grid',
      headStyles: {
        fillColor: [26, 115, 232],
        fontSize: 7,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 6 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 18 },
        2: { cellWidth: 18 },
        3: { cellWidth: 23 },
        4: { cellWidth: 18 },
        5: { cellWidth: 18 },
        6: { cellWidth: 18 },
        7: { cellWidth: 16 },
        8: { cellWidth: 15 },
      },
      margin: { left: 14, right: 14 },
    });
  }

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated on ${dayjs().format('MMM D, YYYY hh:mm A')}`,
      pageWidth - 14,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'right' }
    );
  }

  // Save the PDF
  const fileName = `Awake_Account_Statement_${dayjs().format(
    'YYYY-MM-DD'
  )}.pdf`;
  doc.save(fileName);
};
